import type { UiFontSize, UiFontWeight, UiLetterSpacing, UiRadius, UiShadow, UiThemeContractEntry } from "./theme.types";

function kebab(value: string): string {
  return value.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

function entry(path: string, variable: string, consumer: string, tailwindAlias?: string): UiThemeContractEntry {
  return { path, cssVariable: `--ui-${variable}`, ...(tailwindAlias ? { tailwindAlias } : {}), consumer };
}

const COLOR_ROLES = {
  primary: "Primary actions and links",
  primaryHover: "Primary action hover state",
  primarySoft: "Primary soft surface",
  onPrimary: "Text and icons on primary actions",
  danger: "Danger actions and status",
  dangerDark: "Danger hover state",
  dangerSoft: "Danger soft surface",
  onDanger: "Text and icons on danger actions",
  success: "Success status",
  successDark: "Success emphasis state",
  successSoft: "Success soft surface",
  warning: "Warning status",
  warningDark: "Warning emphasis state",
  warningSoft: "Warning soft surface",
  background: "Application background and portal menu content",
  surface: "Raised surfaces and highlighted menu items",
  border: "Control and surface borders",
  textPrimary: "Primary text and portal menu items",
  textSecondary: "Secondary and descriptive text",
  progressPlaceholder: "Progress track",
  focusRing: "Keyboard focus indicator",
  overlay: "Modal backdrop",
} satisfies Record<string, string>;

const FONT_SIZES: readonly UiFontSize[] = ["xs", "sm", "base", "lg", "xl", "2xl", "3xl", "4xl", "5xl", "6xl", "7xl", "8xl", "9xl"];
const FONT_WEIGHTS: readonly UiFontWeight[] = ["normal", "medium", "semibold", "bold"];
const LETTER_SPACINGS: readonly UiLetterSpacing[] = ["tight", "normal", "wide"];
const RADII: readonly UiRadius[] = ["xs", "sm", "md", "lg", "xl", "2xl", "3xl", "4xl", "full"];
const SHADOWS: readonly UiShadow[] = ["sm", "md", "lg", "xl", "2xl"];

const colorEntries = Object.entries(COLOR_ROLES).map(([key, consumer]) => entry(
  `colors.${key}`,
  `color-${kebab(key)}`,
  consumer,
  `--color-${kebab(key)}`,
));

const typographyEntries: UiThemeContractEntry[] = [
  entry("typography.fontFamily", "font-family", "Application font family", "--font-sans"),
  entry("typography.serifFontFamily", "font-family-serif", "Application serif font family", "--font-serif"),
  ...FONT_SIZES.map((size) => entry(`typography.fontSizes.${size}`, `font-size-${size}`, `Typography size ${size}`, `--text-${size}`)),
  ...FONT_SIZES.map((size) => entry(`typography.lineHeights.${size}`, `line-height-${size}`, `Typography line height ${size}`, `--leading-${size}`)),
  ...FONT_WEIGHTS.map((weight) => entry(`typography.fontWeights.${weight}`, `font-weight-${weight}`, `Font weight ${weight}`, `--font-weight-${weight}`)),
  ...LETTER_SPACINGS.map((spacing) => entry(`typography.letterSpacing.${spacing}`, `letter-spacing-${spacing}`, `Letter spacing ${spacing}`, `--tracking-${spacing}`)),
];

const radiusEntries = RADII.map((radius) => entry(`radii.${radius}`, `radius-${radius}`, `Shared radius ${radius}`, `--radius-${radius}`));
const shadowEntries = SHADOWS.map((shadow) => entry(`shadows.${shadow}`, `shadow-${shadow}`, `Shared shadow ${shadow}`, `--shadow-${shadow}`));

const COMPONENT_CONTRACTS: ReadonlyArray<readonly [string, string, string]> = [
  ["button.radius", "button-radius", "Button radius"],
  ["button.sizes.sm.minHeight", "button-sm-min-height", "Small button minimum height"],
  ["button.sizes.sm.paddingX", "button-sm-padding-x", "Small button horizontal padding"],
  ["button.sizes.sm.paddingY", "button-sm-padding-y", "Small button vertical padding"],
  ["button.sizes.sm.fontSize", "button-sm-font-size", "Small button text size"],
  ["button.sizes.md.minHeight", "button-md-min-height", "Medium button minimum height"],
  ["button.sizes.md.paddingX", "button-md-padding-x", "Medium button horizontal padding"],
  ["button.sizes.md.paddingY", "button-md-padding-y", "Medium button vertical padding"],
  ["button.sizes.md.fontSize", "button-md-font-size", "Medium button text size"],
  ["field.radius", "field-radius", "Field radius"], ["field.minHeight", "field-min-height", "Field minimum height"],
  ["field.paddingX", "field-padding-x", "Field horizontal padding"], ["field.paddingY", "field-padding-y", "Field vertical padding"],
  ["field.fontSize", "field-font-size", "Field text size"],
  ["card.radius", "card-radius", "Card radius"],
  ["card.padding.none", "card-padding-none", "Card no-padding variant"],
  ["card.padding.compact", "card-padding-compact", "Card compact padding"],
  ["card.padding.default", "card-padding-default", "Card default padding"],
  ["card.padding.comfortable", "card-padding-comfortable", "Card comfortable padding"],
  ["card.responsivePadding.compact", "card-responsive-padding-compact", "Card compact responsive padding"],
  ["card.responsivePadding.comfortable", "card-responsive-padding-comfortable", "Card comfortable responsive padding"],
  ["iconButton.radius", "icon-button-radius", "Icon button radius"],
  ["iconButton.sizes.sm", "icon-button-sm-size", "Small icon button side"],
  ["iconButton.sizes.md", "icon-button-md-size", "Medium icon button side"],
  ["menu.minWidth", "menu-min-width", "Menu minimum width"], ["menu.radius", "menu-radius", "Menu radius"],
  ["menu.padding", "menu-padding", "Menu padding"], ["menu.itemPaddingX", "menu-item-padding-x", "Menu item horizontal padding"],
  ["menu.itemPaddingY", "menu-item-padding-y", "Menu item vertical padding"], ["menu.fontSize", "menu-font-size", "Menu text size"],
  ["menu.shadow", "menu-shadow", "Menu shadow"], ["modal.maxWidth", "modal-max-width", "Modal maximum width"],
  ["modal.radius", "modal-radius", "Modal radius"], ["modal.padding", "modal-padding", "Modal padding"],
  ["modal.headerGap", "modal-header-gap", "Modal header gap"], ["modal.bodyGap", "modal-body-gap", "Modal body gap"],
  ["modal.footerGap", "modal-footer-gap", "Modal footer gap"], ["modal.actionGap", "modal-action-gap", "Modal action gap"],
  ["modal.titleFontSize", "modal-title-font-size", "Modal title size"], ["pageShell.maxWidth", "page-shell-max-width", "Page shell maximum width"],
  ["pageShell.gap", "page-shell-gap", "Page shell section gap"], ["pageShell.paddingX", "page-shell-padding-x", "Page shell horizontal padding"],
  ["pageShell.paddingY", "page-shell-padding-y", "Page shell vertical padding"], ["pageShell.desktopPaddingX", "page-shell-desktop-padding-x", "Page shell desktop horizontal padding"],
  ["pageHeader.titleFontSize", "page-header-title-font-size", "Page heading size"],
  ["pageHeader.titleFontFamily", "page-header-title-font-family", "Page heading font family"],
  ["pageHeader.titleFontWeight", "page-header-title-font-weight", "Page heading font weight"],
  ["pageHeader.titleLineHeight", "page-header-title-line-height", "Page heading line height"],
  ["pageHeader.descriptionFontSize", "page-header-description-font-size", "Page description size"],
  ["pageHeader.gap", "page-header-gap", "Page heading content gap"], ["feedback.spinnerSize", "feedback-spinner-size", "Feedback spinner size"],
  ["feedback.spinnerBorderWidth", "feedback-spinner-border-width", "Feedback spinner border"],
  ["feedback.progressHeight", "feedback-progress-height", "Progress track height"],
  ["feedback.focusRingWidth", "feedback-focus-ring-width", "Control focus ring width"],
  ["feedback.focusRingOffset", "feedback-focus-ring-offset", "Control focus ring offset"],
  ["feedback.disabledOpacity", "feedback-disabled-opacity", "Disabled state opacity"],
];

const componentEntries = COMPONENT_CONTRACTS.map(([path, variable, consumer]) => entry(
  `components.${path}`,
  variable,
  consumer,
));

export const UI_THEME_CONTRACT: readonly UiThemeContractEntry[] = Object.freeze([
  ...colorEntries,
  ...typographyEntries,
  entry("spacing.unit", "spacing-unit", "Tailwind spacing scale", "--spacing"),
  ...radiusEntries,
  ...shadowEntries,
  ...componentEntries,
  entry("accessibility.minimumControlHeight", "minimum-control-height", "Minimum accessible button and field height"),
]);
